import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import ShinyText from './ShinyText';

interface SectionHeadingProps {
  eyebrow?: string;
  title: string;
  subtitle?: string;
}

/**
 * Section header with an SVG underline stroke that draws itself
 * when the heading scrolls into view.
 */
const SectionHeading: React.FC<SectionHeadingProps> = ({ eyebrow, title, subtitle }) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const gradientId = `underline-grad-${title.replace(/[^a-zA-Z0-9]/g, '')}`;

  return (
    <div ref={ref} className="text-center mb-16">
      {eyebrow && (
        <motion.span
          initial={{ opacity: 0, y: 12 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="inline-block text-xs md:text-sm font-medium tracking-[0.35em] uppercase text-primary-400/80 mb-4"
        >
          {eyebrow}
        </motion.span>
      )}

      <motion.h2
        initial={{ opacity: 0, y: 24 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="text-4xl md:text-5xl font-bold mb-4"
      >
        <ShinyText text={title} speed={4} />
      </motion.h2>

      {/* Hand-drawn underline stroke that draws itself in */}
      <svg
        className="mx-auto mb-2"
        width="180"
        height="18"
        viewBox="0 0 180 18"
        fill="none"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="180" y2="0" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#667eea" />
            <stop offset="0.5" stopColor="#a78bfa" />
            <stop offset="1" stopColor="#764ba2" />
          </linearGradient>
        </defs>
        <motion.path
          d="M4 11 C 40 4, 80 14, 116 8 S 168 9, 176 7"
          stroke={`url(#${gradientId})`}
          strokeWidth="3"
          strokeLinecap="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={isInView ? { pathLength: 1, opacity: 1 } : {}}
          transition={{ duration: 1, delay: 0.35, ease: [0.65, 0, 0.35, 1] }}
        />
      </svg>

      {subtitle && (
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-xl text-gray-300 max-w-2xl mx-auto mt-4"
        >
          {subtitle}
        </motion.p>
      )}
    </div>
  );
};

export default SectionHeading;
